<template>
    <BaseDialog
        :open="open"
        :disable-close-on-backdrop-click="isSaving"
        :show-close="!isSaving"
        :title="t('developmentJourneys.timezoneDialog.title')"
        width="narrow"
        @update:open="emit('update:open', $event)">
        <form :id="formId" class="flex w-full flex-col gap-4" @submit.prevent="saveTimezone">
            <p class="body-small text-ink-muted">{{ t("developmentJourneys.timezoneDialog.description") }}</p>

            <label class="flex flex-col gap-2" for="development-journeys-timezone">
                <span class="body-small-semibold text-control-label">{{ t("developmentJourneys.timezoneDialog.label") }}</span>
                <BaseCombobox
                    v-model="selectedTimezone"
                    :disabled="isSaving"
                    :options="timezoneOptions"
                    input-id="development-journeys-timezone"
                    :placeholder="t('developmentJourneys.timezoneDialog.placeholder')"
                    auto-focus />
            </label>

            <div role="alert" aria-atomic="true">
                <BaseNotification v-if="saveError" role="none" theme="error" :text="saveError" hide-dismiss />
            </div>
        </form>
        <template #footer>
            <JourneyButton :form="formId" type="submit" theme="primary" size="md" :disabled="!selectedTimezone || isSaving" data-testid="save-development-journeys-timezone">
                {{ isSaving ? t("common.saving") : t("developmentJourneys.timezoneDialog.saveAndContinue") }}
            </JourneyButton>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseCombobox from "~vue/components/BaseCombobox.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import JourneyButton from "~vue/pages/developmentJourneys/DevelopmentJourneyButton.vue";
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
    open: boolean;
    timezones: string[];
    timezone?: string;
}>();
const formId = useId();

const emit = defineEmits<{
    saved: [];
    "update:open": [open: boolean];
}>();

type TimezoneOption = {
    label: string;
    value: string;
};

const timezoneOptions = computed<TimezoneOption[]>(() => props.timezones.map((timezone) => ({ label: timezone, value: timezone })));
const selectedTimezone = ref<TimezoneOption | null>(null);
const hasSaveError = ref(false);
const saveError = computed(() => (hasSaveError.value ? t("developmentJourneys.timezoneDialog.saveError") : null));
const timezonePayload = computed(() => ({ timezone: selectedTimezone.value?.value }));
const { error: timezoneUpdateError, execute: updateTimezone, isFetching: isSaving } = useFetch("/api/accounts/user", { immediate: false }).put(timezonePayload).json();

watch(
    () => [props.open, props.timezone],
    ([isOpen, timezone]) => {
        if (isOpen) {
            selectedTimezone.value = timezoneOptions.value.find((option) => option.value === timezone) ?? null;
        }
    },
    { immediate: true },
);

const saveTimezone = async () => {
    if (!selectedTimezone.value || isSaving.value) {
        return;
    }

    hasSaveError.value = false;
    await updateTimezone();

    if (timezoneUpdateError.value) {
        hasSaveError.value = true;
        return;
    }

    emit("saved");
};
</script>
